import React, { useState } from 'react'
import { Card, Typography, Space, Button } from 'antd'
import { PlusOutlined, ExportOutlined, DatabaseOutlined } from '@ant-design/icons'
import { theme } from 'antd'
import { hexToRGBA } from '../../../../../lib/utils'
import useInventory from '../../hooks/useInventory'
import AddEditItemModal from '../AddItemModal'
import InventorySearchBar from './InventorySearchBar'
import InventoryFilters from './InventoryFilters'
import InventoryTable from './InventoryTable'
import '../../../../styles/InventoryComponents.css'

const { Title } = Typography

const InventoryList = () => {
  // Use theme token
  const { token } = theme.useToken()

  const {
    inventory,
    filteredItems,
    selectedItem,
    loading,
    error,
    searchTerm,
    handleSearch,
    filterByCategory,
    filterByDepartment,
    filterByStockStatus,
    filterByExpiryStatus,
    resetFilters,
    deleteInventoryItem,
    isAddModalVisible,
    isEditModalVisible,
    isDeleteModalVisible,
    showAddModal,
    hideAddModal,
    showEditModal,
    hideEditModal,
    showDeleteModal,
    hideDeleteModal,
    addInventoryItem,
    updateInventoryItem
  } = useInventory()

  // State for table settings
  const [pageSize, setPageSize] = useState(10)
  const [sortedInfo, setSortedInfo] = useState({})

  // Handle table change
  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter)
  }

  // Export data to CSV
  const exportToCsv = () => {
    const headers = [
      'ID',
      'Name',
      'Description',
      'Category',
      'Quantity',
      'Min Level',
      'Max Level',
      'Unit Cost',
      'Supplier',
      'Expiry Date',
      'Department',
      'Location'
    ]

    const csvContent = [
      headers.join(','),
      ...filteredItems.map((item) =>
        [
          item.id,
          `"${item.name.replace(/"/g, '""')}"`,
          `"${(item.description || '').replace(/"/g, '""')}"`,
          item.category,
          item.quantityOnHand,
          item.minStockLevel,
          item.maxStockLevel,
          item.unitCost,
          `"${item.supplier.name.replace(/"/g, '""')}"`,
          item.expiryDate || '',
          item.department,
          item.storageLocation || ''
        ].join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `inventory_export_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className='inventory-page'>
      <div className='dashboard-header inventory-header'>
        <div className='dashboard-header-title'>
          <DatabaseOutlined className='dashboard-header-icon' />
          <Title level={4}>Inventory Items</Title>
        </div>
        <Space>
          <Button
            icon={<PlusOutlined style={{ color: token.colorSuccess }} />}
            onClick={showAddModal}
            style={{
              background: hexToRGBA(token.colorSuccess, 0.1),
              borderColor: token.colorSuccess,
              color: token.colorSuccess,
              fontWeight: 'bold'
            }}
          >
            Add Item
          </Button>
          <Button icon={<ExportOutlined />} onClick={exportToCsv}>
            Export
          </Button>
        </Space>
      </div>

      <Card className='inventory-container'>
        <div className='inventory-action-bar'>
          <InventorySearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
          <InventoryFilters
            filterByCategory={filterByCategory}
            filterByDepartment={filterByDepartment}
            filterByStockStatus={filterByStockStatus}
            resetFilters={resetFilters}
          />
        </div>

        <InventoryTable
          filteredItems={filteredItems}
          loading={loading}
          sortedInfo={sortedInfo}
          handleTableChange={handleTableChange}
          pageSize={pageSize}
          setPageSize={setPageSize}
          showEditModal={showEditModal}
          deleteInventoryItem={deleteInventoryItem}
        />
      </Card>

      {/* Add/Edit modal */}
      <AddEditItemModal
        visible={isAddModalVisible}
        onCancel={hideAddModal}
        onSave={addInventoryItem}
        title='Add Inventory Item'
        item={null}
      />

      <AddEditItemModal
        visible={isEditModalVisible}
        onCancel={hideEditModal}
        onSave={updateInventoryItem}
        title='Edit Inventory Item'
        item={selectedItem}
      />
    </div>
  )
}

export default InventoryList
